import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Edit, FileText, User, Mail, Calendar, AlertCircle, Save, X, Phone, Stethoscope, Camera } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { toast } from '../ui/use-toast';

const SubmissionDetailsAndFeedback = ({
  selectedSubmission,
  feedback,
  setFeedback,
  recommendations,
  setRecommendations,
  nextAppointment,
  setNextAppointment,
  submissionStatus,
  setSubmissionStatus,
  handleUpdateSubmission,
  setSelectedSubmission,
  profile
}) => {
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: profile?.full_name || profile?.name || '',
    specialization: profile?.specialization || '',
    phone: profile?.phone || '',
    email: profile?.email || '',
    description: profile?.description || '',
    profile_photo: profile?.profile_photo || null
  });
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(profile?.profile_photo || null);
  const [profilePhotoError, setProfilePhotoError] = useState(false);

  // Update profileData when profile prop changes
  useEffect(() => {
    if (profile) {
      setProfileData({
        full_name: profile?.full_name || profile?.name || '',
        specialization: profile?.specialization || '',
        phone: profile?.phone || '',
        email: profile?.email || '',
        description: profile?.description || '',
        profile_photo: profile?.profile_photo || null
      });
      setProfilePhotoPreview(profile?.profile_photo || null);
      setProfilePhotoError(false); // Reset error when profile changes
    }
  }, [profile]);

  const getPriorityColor = (painLevel) => {
    const level = parseInt(painLevel);
    if (level >= 7) return 'text-red-400';
    if (level >= 4) return 'text-yellow-400';
    return 'text-green-400';
  };

  const patientProfile = selectedSubmission?.profiles;

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Photo size must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      setProfilePhotoFile(file);
      setProfilePhotoError(false); // Reset error when new photo is selected
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    try {
      let profilePhotoUrl = profileData.profile_photo;

      // Upload photo if new file selected
      if (profilePhotoFile) {
        const formData = new FormData();
        formData.append('photo', profilePhotoFile);
        
        const token = localStorage.getItem('session_token');
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://test.gugandentalcare.com/api/api';

        const uploadResponse = await fetch(`${API_BASE_URL}/upload_photo.php`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          profilePhotoUrl = uploadData.url || uploadData.path || uploadData.filename;
        }
      }

      const updateData = {
        id: profile?.id,
        name: profileData.full_name,
        phone: profileData.phone,
        email: profileData.email || null,
        specialization: profileData.specialization || null,
        description: profileData.description || null,
        profile_photo: profilePhotoUrl
      };

      const data = await apiRequest('update_user.php', {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Profile updated successfully. Please refresh the page to see changes.'
        });
        setIsEditingProfile(false);
        setProfilePhotoFile(null);
        // Reload page after a short delay to show the toast
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to update profile',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        variant: 'destructive'
      });
    }
  };

  return (
    <Card className="glass-effect border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center justify-between">
          <div className="flex items-center">
            <Edit className="h-5 w-5 mr-2 text-blue-400" />
            {selectedSubmission ? 'Patient Details & Feedback' : 'Doctor Profile'}
          </div>
          {!selectedSubmission && !isEditingProfile && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditingProfile(true)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {selectedSubmission ? (
          <div className="space-y-6">
            {/* Patient Information */}
            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="text-white font-semibold mb-3">Patient Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-white">{patientProfile?.name || 'Unknown Patient'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-white">{patientProfile?.email || 'No email'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-white">
                    {new Date(selectedSubmission.submitted_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-4 w-4 text-gray-400" />
                  <span className={`font-medium ${getPriorityColor(selectedSubmission.pain_level)}`}>
                    Pain: {selectedSubmission.pain_level || 'N/A'}/10
                  </span>
                </div>
              </div>
            </div>

            {/* Medical Details */}
            <div className="space-y-4">
              <div>
                <Label className="text-white font-medium">Chief Complaint</Label>
                <p className="text-gray-300 mt-1 p-3 bg-gray-800 rounded">
                  {selectedSubmission.chief_complaint || 'Not specified'}
                </p>
              </div>
              
              {selectedSubmission.symptoms && selectedSubmission.symptoms.length > 0 && (
                <div>
                  <Label className="text-white font-medium">Symptoms</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedSubmission.symptoms.map((symptom, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-600 text-white text-xs rounded">
                        {symptom}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedSubmission.form_data?.allergies && (
                <div>
                  <Label className="text-white font-medium">Allergies</Label>
                  <p className="text-gray-300 mt-1 p-3 bg-gray-800 rounded">
                    {selectedSubmission.form_data.allergies}
                  </p>
                </div>
              )}
            </div>

            {/* Feedback Form */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="status" className="text-white">Status</Label>
                <Select value={submissionStatus} onValueChange={setSubmissionStatus}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed Submissions</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="pending">Pending Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="feedback" className="text-white">Doctor's Feedback</Label>
                <Textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                  placeholder="Provide your medical assessment and findings..."
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="recommendations" className="text-white">Recommendations</Label>
                <Textarea
                  id="recommendations"
                  value={recommendations}
                  onChange={(e) => setRecommendations(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                  placeholder="Treatment recommendations and next steps..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="nextAppointment" className="text-white">Next Appointment</Label>
                <Input
                  id="nextAppointment"
                  type="datetime-local"
                  value={nextAppointment ? new Date(nextAppointment).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setNextAppointment(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                />
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={handleUpdateSubmission}
                  className="medical-gradient text-white hover:opacity-90 flex-1"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Feedback
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedSubmission(null)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {!isEditingProfile ? (
              <>
                {/* Doctor Profile Display */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pb-4 border-b border-gray-700">
                  {/* Profile Photo */}
                  <div className="relative">
                    {profilePhotoPreview && !profilePhotoError ? (
                      <img
                        src={profilePhotoPreview}
                        alt="Doctor Profile"
                        className="w-24 h-24 rounded-full object-cover border-2 border-blue-400"
                        onError={() => setProfilePhotoError(true)}
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-700 flex items-center justify-center border-2 border-blue-400">
                        <Stethoscope className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Doctor Info */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">
                      Dr. {profile?.full_name || profile?.name || 'Doctor'}
                    </h3>
                    {profile?.specialization && (
                      <p className="text-blue-400 text-sm mb-2">{profile.specialization}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm">
                      {profile?.phone && (
                        <div className="flex items-center gap-2 text-gray-300">
                          <Phone className="h-4 w-4" />
                          <span>{profile.phone}</span>
                        </div>
                      )}
                      {profile?.email && (
                        <div className="flex items-center gap-2 text-gray-300">
                          <Mail className="h-4 w-4" />
                          <span>{profile.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Doctor Bio */}
                {profile?.description && (
                  <div>
                    <Label className="text-white font-medium mb-2 block">Bio</Label>
                    <p className="text-gray-300 text-sm leading-relaxed p-3 bg-gray-800 rounded">
                      {profile.description}
                    </p>
                  </div>
                )}

                {!profile?.description && (
                  <div className="text-center py-8">
                    <p className="text-gray-400 text-sm">No bio available. Click Edit to add your bio.</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Edit Profile Form */}
                <div className="space-y-4">
                  {/* Profile Photo Upload */}
                  <div>
                    <Label className="text-white font-medium mb-2 block">Profile Photo</Label>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        {profilePhotoPreview && !profilePhotoError ? (
                          <img
                            src={profilePhotoPreview}
                            alt="Profile Preview"
                            className="w-24 h-24 rounded-full object-cover border-2 border-blue-400"
                            onError={() => setProfilePhotoError(true)}
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-full bg-gray-700 flex items-center justify-center border-2 border-blue-400">
                            <Stethoscope className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div>
                        <Input
                          type="file"
                          accept="image/jpeg,image/png,image/gif"
                          onChange={handleProfilePhotoChange}
                          className="hidden"
                          id="profile-photo-upload"
                        />
                        <Label
                          htmlFor="profile-photo-upload"
                          className="cursor-pointer inline-flex items-center px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 text-sm"
                        >
                          <Camera className="h-4 w-4 mr-2" />
                          {profilePhotoFile ? 'Change Photo' : 'Upload Photo'}
                        </Label>
                        <p className="text-gray-400 text-xs mt-1">Max 5MB, JPG/PNG/GIF</p>
                      </div>
                    </div>
                  </div>

                  {/* Full Name */}
                  <div>
                    <Label htmlFor="full_name" className="text-white">Full Name</Label>
                    <Input
                      id="full_name"
                      value={profileData.full_name}
                      onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white mt-1"
                      placeholder="Dr. John Doe"
                    />
                  </div>

                  {/* Specialization */}
                  <div>
                    <Label htmlFor="specialization" className="text-white">Specialization</Label>
                    <Input
                      id="specialization"
                      value={profileData.specialization}
                      onChange={(e) => setProfileData({ ...profileData, specialization: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white mt-1"
                      placeholder="General Dentistry, Orthodontics, etc."
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone" className="text-white">Phone</Label>
                    <Input
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white mt-1"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-white">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white mt-1"
                      placeholder="doctor@example.com"
                    />
                  </div>

                  {/* Bio */}
                  <div>
                    <Label htmlFor="description" className="text-white">Bio</Label>
                    <Textarea
                      id="description"
                      value={profileData.description}
                      onChange={(e) => setProfileData({ ...profileData, description: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white mt-1"
                      placeholder="Write a brief bio about yourself, your experience, and expertise..."
                      rows={4}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-2">
                    <Button
                      onClick={handleSaveProfile}
                      className="medical-gradient text-white hover:opacity-90 flex-1"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Profile
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditingProfile(false);
                        setProfileData({
                          full_name: profile?.full_name || profile?.name || '',
                          specialization: profile?.specialization || '',
                          phone: profile?.phone || '',
                          email: profile?.email || '',
                          description: profile?.description || '',
                          profile_photo: profile?.profile_photo || null
                        });
                        setProfilePhotoFile(null);
                        setProfilePhotoPreview(profile?.profile_photo || null);
                        setProfilePhotoError(false);
                      }}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SubmissionDetailsAndFeedback;